import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Chapter, Scene, ScenePlanPoint, SceneHealth, BookPromise, BookGuide, BookFact } from '../types';
import {
  getGraphQLClient,
  GET_BOOK_CHAPTERS_TREE,
  SAVE_BOOK_PROMISE,
  DELETE_BOOK_PROMISE,
  REORDER_BOOK_PROMISES,
  SET_SCENE_PLAN_POINT_PROMISE,
  CREATE_CHAPTER,
  CREATE_SCENE,
  UPDATE_CHAPTER,
  UPDATE_SCENE,
  DELETE_CHAPTER,
  DELETE_SCENE,
  REORDER_CHAPTERS,
  REORDER_SCENES,
  GENERATE_CHAPTER_COVERS,
  SAVE_SCENE_PLAN_POINT,
  DELETE_SCENE_PLAN_POINT,
  LOCK_SCENE_PLAN_POINT,
  UNLOCK_SCENE_PLAN_POINT,
  REORDER_SCENE_PLAN_POINTS,
  GENERATE_SCENE_PLAN_POINT_SUGGESTIONS,
} from '../lib/graphql';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { captureException } from '../lib/sentry';
import { Button, Card, Spinner, ConfirmDialog, DropdownMenu, DropdownMenuItem } from '../components/common';
import BookCompanion from '../components/book/BookCompanion';
import BookPromisesPanel from '../components/book/BookPromisesPanel';
import BookGuidesPanel from '../components/book/BookGuidesPanel';
import FactsPanel from '../components/book/FactsPanel';
import {
  ClaimSuggestionPanel,
  TaggedSuggestion,
  SuggestionMode,
} from '../components/writing/ClaimSuggestionPanel';

type ChaptersTreeResponse = {
  book: {
    id: string;
    title: string;
    fullTitle: string;
    chapters: Chapter[];
    bookPromises: BookPromise[];
    bookGuides: BookGuide[];
    bookFacts: BookFact[];
  } | null;
};

const HEALTH_DOT_COLORS: Record<SceneHealth, string> = {
  thin: 'bg-red-400',
  in_progress: 'bg-amber-400',
  ready: 'bg-emerald-500',
  polished: 'bg-indigo-500',
};

function HealthDot({ health }: { health?: SceneHealth }) {
  const { t } = useTranslation();
  if (!health) return null;
  const label = t(`chaptersFirst.health.${health}`, health);
  return (
    <span
      className={`inline-block w-2 h-2 rounded-full shrink-0 ${HEALTH_DOT_COLORS[health]}`}
      title={label}
      aria-label={label}
    />
  );
}

// Inline click-to-edit text used for chapter/scene titles and plan point content.
function InlineEdit({
  value,
  onCommit,
  className,
  inputClassName,
  placeholder,
  ariaLabel,
}: {
  value: string;
  onCommit: (next: string) => void;
  className?: string;
  inputClassName?: string;
  placeholder?: string;
  ariaLabel?: string;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);

  const commit = () => {
    setEditing(false);
    const trimmed = draft.trim();
    if (trimmed && trimmed !== value) onCommit(trimmed);
    else setDraft(value);
  };

  if (editing) {
    return (
      <input
        type="text"
        autoFocus
        value={draft}
        aria-label={ariaLabel}
        placeholder={placeholder}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            commit();
          } else if (e.key === 'Escape') {
            setDraft(value);
            setEditing(false);
          }
        }}
        className={
          inputClassName ??
          'w-full bg-transparent border-b border-indigo-400 focus:outline-none py-0.5'
        }
      />
    );
  }
  return (
    <button
      type="button"
      onClick={() => {
        setDraft(value);
        setEditing(true);
      }}
      className={className ?? 'text-left hover:text-indigo-600 dark:hover:text-indigo-300'}
    >
      {value || placeholder}
    </button>
  );
}

// Removal control — matches the X-style delete affordance used across the app
// (e.g. ChapterFlowSelectionPage), rather than a trash/× glyph.
function RemoveButton({ onClick, label }: { onClick: () => void; label: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      title={label}
      className="shrink-0 inline-flex items-center justify-center w-6 h-6 rounded text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
    >
      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M6 18L18 6M6 6l12 12"
        />
      </svg>
    </button>
  );
}

export default function PlanManagerPage() {
  const { t } = useTranslation();
  const { bookId } = useParams<{ bookId: string }>();
  const navigate = useNavigate();

  const [chapters, setChapters] = useState<Chapter[]>([]);
  const [promises, setPromises] = useState<BookPromise[]>([]);
  const [guides, setGuides] = useState<BookGuide[]>([]);
  const [facts, setFacts] = useState<BookFact[]>([]);
  const [bookTitle, setBookTitle] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [deleteChapterTarget, setDeleteChapterTarget] = useState<Chapter | null>(null);
  const [deleteSceneTarget, setDeleteSceneTarget] = useState<Scene | null>(null);
  const [busyChapterId, setBusyChapterId] = useState<string | null>(null);
  const [companionOpen, setCompanionOpen] = useState(false);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const fetchTree = useCallback(async () => {
    if (!bookId) return;
    setError(null);
    try {
      const client = getGraphQLClient();
      const data = await client.request<ChaptersTreeResponse>(GET_BOOK_CHAPTERS_TREE, {
        id: bookId,
      });
      if (!data.book) {
        setError(t('common.notFound', 'Not found'));
        setChapters([]);
        return;
      }
      const sorted = [...data.book.chapters].sort((a, b) => a.position - b.position);
      setChapters(sorted);
      setPromises(
        [...(data.book.bookPromises ?? [])].sort((a, b) => a.position - b.position)
      );
      setGuides([...(data.book.bookGuides ?? [])].sort((a, b) => a.slug.localeCompare(b.slug)));
      setFacts([...(data.book.bookFacts ?? [])].sort((a, b) => a.position - b.position));
      setBookTitle(data.book.fullTitle || data.book.title || '');
      // Expand everything by default the first time the tree loads.
      setExpanded((prev) => (prev.size === 0 ? new Set(sorted.map((c) => c.id)) : prev));
    } catch (err) {
      captureException(err);
      setError(t('common.error'));
    } finally {
      setLoading(false);
    }
  }, [bookId, t]);

  useEffect(() => {
    fetchTree();
  }, [fetchTree]);

  const toggleExpanded = useCallback((id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const collapseAll = () => setExpanded(new Set());
  const expandAll = () => setExpanded(new Set(chapters.map((c) => c.id)));

  const run = useCallback(
    async (fn: () => Promise<unknown>) => {
      try {
        await fn();
        await fetchTree();
      } catch (err) {
        captureException(err);
        setError(t('common.error'));
      }
    },
    [fetchTree, t]
  );

  // --- Chapter / scene structure ---
  const addChapter = useCallback(async () => {
    if (!bookId) return;
    const client = getGraphQLClient();
    const res = await client
      .request<{ createChapter: { chapter: { id: string } | null } }>(CREATE_CHAPTER, {
        bookId,
        title: t('chaptersFirst.newChapterPlaceholder', 'Untitled chapter'),
      })
      .catch((err) => {
        captureException(err);
        return null;
      });
    await fetchTree();
    const newId = res?.createChapter?.chapter?.id;
    if (newId) setExpanded((prev) => new Set(prev).add(newId));
  }, [bookId, fetchTree, t]);

  const addScene = useCallback(
    (chapterId: string) =>
      run(() =>
        getGraphQLClient().request(CREATE_SCENE, {
          chapterId,
          title: t('chaptersFirst.newScenePlaceholder', 'Untitled scene'),
        })
      ),
    [run, t]
  );

  const renameChapter = useCallback(
    (chapterId: string, title: string) =>
      run(() => getGraphQLClient().request(UPDATE_CHAPTER, { chapterId, title })),
    [run]
  );

  const renameScene = useCallback(
    (sceneId: string, title: string) =>
      run(() => getGraphQLClient().request(UPDATE_SCENE, { sceneId, title })),
    [run]
  );

  const reorderChapters = useCallback(
    (orderedIds: string[]) => {
      if (!bookId) return;
      const previous = chapters;
      const map = new Map(previous.map((c) => [c.id, c]));
      setChapters(orderedIds.map((id, i) => ({ ...(map.get(id) as Chapter), position: i })));
      getGraphQLClient()
        .request(REORDER_CHAPTERS, { bookId, chapterIds: orderedIds })
        .catch((err) => {
          captureException(err);
          setChapters(previous);
          setError(t('common.error'));
        });
    },
    [bookId, chapters, t]
  );

  const reorderScenes = useCallback(
    (chapterId: string, orderedIds: string[]) => {
      const previous = chapters;
      setChapters((prev) =>
        prev.map((c) => {
          if (c.id !== chapterId) return c;
          const map = new Map((c.scenes ?? []).map((s) => [s.id, s]));
          const next = orderedIds
            .map((id, i) => {
              const s = map.get(id);
              return s ? { ...s, position: i } : null;
            })
            .filter(Boolean) as Scene[];
          return { ...c, scenes: next };
        })
      );
      getGraphQLClient()
        .request(REORDER_SCENES, { chapterId, sceneIds: orderedIds })
        .catch((err) => {
          captureException(err);
          setChapters(previous);
          setError(t('common.error'));
        });
    },
    [chapters, t]
  );

  const performDeleteChapter = useCallback(
    (chapter: Chapter) => {
      setDeleteChapterTarget(null);
      return run(() => getGraphQLClient().request(DELETE_CHAPTER, { chapterId: chapter.id }));
    },
    [run]
  );

  const performDeleteScene = useCallback(
    (scene: Scene) => {
      setDeleteSceneTarget(null);
      return run(() => getGraphQLClient().request(DELETE_SCENE, { sceneId: scene.id }));
    },
    [run]
  );

  const suggestScenes = useCallback(
    async (chapter: Chapter) => {
      if (!bookId) return;
      setBusyChapterId(chapter.id);
      try {
        const client = getGraphQLClient();
        const siblings = chapters
          .filter((c) => c.id !== chapter.id)
          .map((c) => c.title.trim())
          .filter(Boolean);
        const data = await client.request<{
          generateChapterCovers: { success: boolean; errors: string[]; covers: string[] | null };
        }>(GENERATE_CHAPTER_COVERS, {
          bookId,
          chapterTitle: chapter.title,
          instructions: null,
          siblings,
        });
        const result = data.generateChapterCovers;
        if (!result.success || !result.covers) {
          setError(result.errors.join(', ') || t('common.error'));
          return;
        }
        const start = (chapter.scenes ?? []).length;
        await Promise.all(
          result.covers
            .map((c) => c.trim())
            .filter(Boolean)
            .map((title, i) =>
              client.request(CREATE_SCENE, { chapterId: chapter.id, title, position: start + i })
            )
        );
        await fetchTree();
      } catch (err) {
        captureException(err);
        setError(t('common.error'));
      } finally {
        setBusyChapterId(null);
      }
    },
    [bookId, chapters, fetchTree, t]
  );

  // --- Plan points ---
  const patchPlanPoints = useCallback(
    (sceneId: string, updater: (points: ScenePlanPoint[]) => ScenePlanPoint[]) => {
      setChapters((prev) =>
        prev.map((c) => ({
          ...c,
          scenes: (c.scenes ?? []).map((s) =>
            s.id === sceneId ? { ...s, planPoints: updater(s.planPoints ?? []) } : s
          ),
        }))
      );
    },
    []
  );

  const savePlanPoint = useCallback(
    async (sceneId: string, planPointId: string | null, content: string) => {
      const trimmed = content.trim();
      if (!trimmed) return;
      try {
        const res = await getGraphQLClient().request<{
          saveScenePlanPoint: { planPoint: ScenePlanPoint | null; errors: string[] };
        }>(SAVE_SCENE_PLAN_POINT, { sceneId, planPointId, content: trimmed });
        const saved = res.saveScenePlanPoint?.planPoint;
        if (!saved) {
          setError(res.saveScenePlanPoint?.errors?.join(', ') || t('common.error'));
          return;
        }
        patchPlanPoints(sceneId, (points) =>
          points.some((p) => p.id === saved.id)
            ? points.map((p) => (p.id === saved.id ? saved : p))
            : [...points, saved]
        );
      } catch (err) {
        captureException(err);
        setError(t('common.error'));
      }
    },
    [patchPlanPoints, t]
  );

  const deletePlanPoint = useCallback(
    async (sceneId: string, planPointId: string) => {
      const previous = chapters;
      patchPlanPoints(sceneId, (points) => points.filter((p) => p.id !== planPointId));
      try {
        await getGraphQLClient().request(DELETE_SCENE_PLAN_POINT, { planPointId });
      } catch (err) {
        captureException(err);
        setChapters(previous);
        setError(t('common.error'));
      }
    },
    [chapters, patchPlanPoints, t]
  );

  const togglePlanPointLock = useCallback(
    async (sceneId: string, point: ScenePlanPoint) => {
      const nextLocked = !point.locked;
      patchPlanPoints(sceneId, (points) =>
        points.map((p) => (p.id === point.id ? { ...p, locked: nextLocked } : p))
      );
      try {
        await getGraphQLClient().request(
          nextLocked ? LOCK_SCENE_PLAN_POINT : UNLOCK_SCENE_PLAN_POINT,
          { planPointId: point.id }
        );
      } catch (err) {
        captureException(err);
        patchPlanPoints(sceneId, (points) =>
          points.map((p) => (p.id === point.id ? { ...p, locked: point.locked } : p))
        );
        setError(t('common.error'));
      }
    },
    [patchPlanPoints, t]
  );

  const reorderPlanPoints = useCallback(
    async (sceneId: string, orderedIds: string[]) => {
      const previous = chapters;
      patchPlanPoints(sceneId, (points) => {
        const map = new Map(points.map((p) => [p.id, p]));
        return orderedIds
          .map((id, i) => {
            const p = map.get(id);
            return p ? { ...p, position: i } : null;
          })
          .filter(Boolean) as ScenePlanPoint[];
      });
      try {
        await getGraphQLClient().request(REORDER_SCENE_PLAN_POINTS, {
          sceneId,
          planPointIds: orderedIds,
        });
      } catch (err) {
        captureException(err);
        setChapters(previous);
        setError(t('common.error'));
      }
    },
    [chapters, patchPlanPoints, t]
  );

  const suggestPlanPoints = useCallback(
    async (
      sceneId: string,
      opts: { mode: SuggestionMode; focusTag?: string }
    ): Promise<TaggedSuggestion[]> => {
      try {
        const data = await getGraphQLClient().request<{
          generateScenePlanPointSuggestions: {
            errors: string[];
            suggestions: { items: TaggedSuggestion[] } | null;
          };
        }>(GENERATE_SCENE_PLAN_POINT_SUGGESTIONS, {
          sceneId,
          mode: opts.mode,
          focusTag: opts.focusTag,
        });
        const result = data.generateScenePlanPointSuggestions;
        if (result.errors?.length) {
          setError(result.errors.join(', '));
          return [];
        }
        return result.suggestions?.items ?? [];
      } catch (err) {
        captureException(err);
        setError(t('common.error'));
        return [];
      }
    },
    [t]
  );

  // --- Book promises ---
  // Live coverage: which promises at least one scene plan point delivers.
  const linkedPromiseIds = useMemo(() => {
    const set = new Set<string>();
    chapters.forEach((c) =>
      (c.scenes ?? []).forEach((s) =>
        (s.planPoints ?? []).forEach((p) => {
          if (p.bookPromiseId) set.add(p.bookPromiseId);
        })
      )
    );
    return set;
  }, [chapters]);

  const addPromise = useCallback(
    async (content: string) => {
      if (!bookId) return;
      try {
        const res = await getGraphQLClient().request<{
          saveBookPromise: { bookPromise: BookPromise | null; errors: string[] };
        }>(SAVE_BOOK_PROMISE, { bookId, content });
        const saved = res.saveBookPromise?.bookPromise;
        if (!saved) {
          setError(res.saveBookPromise?.errors?.join(', ') || t('common.error'));
          return;
        }
        setPromises((prev) => [...prev, saved]);
      } catch (err) {
        captureException(err);
        setError(t('common.error'));
      }
    },
    [bookId, t]
  );

  const savePromise = useCallback(
    async (promiseId: string, content: string) => {
      if (!bookId) return;
      try {
        const res = await getGraphQLClient().request<{
          saveBookPromise: { bookPromise: BookPromise | null; errors: string[] };
        }>(SAVE_BOOK_PROMISE, { bookId, bookPromiseId: promiseId, content });
        const saved = res.saveBookPromise?.bookPromise;
        if (!saved) {
          setError(res.saveBookPromise?.errors?.join(', ') || t('common.error'));
          return;
        }
        setPromises((prev) => prev.map((p) => (p.id === saved.id ? saved : p)));
      } catch (err) {
        captureException(err);
        setError(t('common.error'));
      }
    },
    [bookId, t]
  );

  const deletePromise = useCallback(
    async (promiseId: string) => {
      const prevPromises = promises;
      const prevChapters = chapters;
      setPromises((prev) => prev.filter((p) => p.id !== promiseId));
      // Mirror the FK nullify so coverage dots update without a refetch.
      setChapters((prev) =>
        prev.map((c) => ({
          ...c,
          scenes: (c.scenes ?? []).map((s) => ({
            ...s,
            planPoints: (s.planPoints ?? []).map((p) =>
              p.bookPromiseId === promiseId ? { ...p, bookPromiseId: null } : p
            ),
          })),
        }))
      );
      try {
        await getGraphQLClient().request(DELETE_BOOK_PROMISE, { bookPromiseId: promiseId });
      } catch (err) {
        captureException(err);
        setPromises(prevPromises);
        setChapters(prevChapters);
        setError(t('common.error'));
      }
    },
    [promises, chapters, t]
  );

  const reorderPromises = useCallback(
    (orderedIds: string[]) => {
      if (!bookId) return;
      const previous = promises;
      const map = new Map(previous.map((p) => [p.id, p]));
      setPromises(orderedIds.map((id, i) => ({ ...(map.get(id) as BookPromise), position: i })));
      getGraphQLClient()
        .request(REORDER_BOOK_PROMISES, { bookId, bookPromiseIds: orderedIds })
        .catch((err) => {
          captureException(err);
          setPromises(previous);
          setError(t('common.error'));
        });
    },
    [bookId, promises, t]
  );

  const setPlanPointPromise = useCallback(
    async (sceneId: string, planPointId: string, promiseId: string | null) => {
      patchPlanPoints(sceneId, (points) =>
        points.map((p) => (p.id === planPointId ? { ...p, bookPromiseId: promiseId } : p))
      );
      try {
        await getGraphQLClient().request(SET_SCENE_PLAN_POINT_PROMISE, {
          planPointId,
          bookPromiseId: promiseId,
        });
      } catch (err) {
        captureException(err);
        setError(t('common.error'));
        fetchTree();
      }
    },
    [patchPlanPoints, fetchTree, t]
  );

  const totalWords = useMemo(
    () => chapters.reduce((sum, c) => sum + (c.wordCount ?? 0), 0),
    [chapters]
  );

  return (
    <div className="max-w-4xl mx-auto px-4 py-6">
      <div className="flex items-center justify-between gap-3 mb-6">
        <div className="min-w-0">
          <button
            type="button"
            onClick={() => navigate(`/books/${bookId}/refine`)}
            className="text-sm text-gray-500 hover:text-indigo-600 dark:hover:text-indigo-300"
          >
            ‹ {t('planManager.back', 'Back to workspace')}
          </button>
          <h1 className="text-2xl font-bold truncate">{t('planManager.title', 'Plan')}</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 truncate">
            {bookTitle} · {chapters.length} {t('chaptersFirst.chaptersLowercase', 'chapters')} ·{' '}
            {totalWords} {t('chaptersFirst.words', 'words')}
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Button variant="secondary" size="sm" onClick={() => setCompanionOpen(true)}>
            💬 {t('planManager.chatAboutThis', 'Chat about this')}
          </Button>
          <Button variant="ghost" size="sm" onClick={collapseAll}>
            {t('planManager.collapseAll', 'Collapse all')}
          </Button>
          <Button variant="ghost" size="sm" onClick={expandAll}>
            {t('planManager.expandAll', 'Expand all')}
          </Button>
          <Button variant="primary" size="sm" onClick={addChapter}>
            + {t('chaptersFirst.addChapter', 'Add chapter')}
          </Button>
        </div>
      </div>

      {error && (
        <div className="mb-4 rounded bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 px-3 py-2 text-sm">
          {error}
        </div>
      )}

      {!loading && bookId && (
        <BookPromisesPanel
          bookId={bookId}
          promises={promises}
          linkedPromiseIds={linkedPromiseIds}
          onAdd={addPromise}
          onSave={savePromise}
          onDelete={deletePromise}
          onReorder={reorderPromises}
        />
      )}

      {!loading && bookId && <BookGuidesPanel bookId={bookId} guides={guides} />}
      {!loading && bookId && <FactsPanel bookId={bookId} facts={facts} />}

      {loading ? (
        <div className="flex justify-center py-20">
          <Spinner />
        </div>
      ) : chapters.length === 0 ? (
        <Card padding="lg" className="text-center text-gray-500">
          {t('chaptersFirst.noChapters', 'No chapters yet.')}
        </Card>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={(event: DragEndEvent) => {
            const { active, over } = event;
            if (!over || active.id === over.id) return;
            const ids = chapters.map((c) => c.id);
            const oldIndex = ids.indexOf(String(active.id));
            const newIndex = ids.indexOf(String(over.id));
            if (oldIndex === -1 || newIndex === -1) return;
            reorderChapters(arrayMove(ids, oldIndex, newIndex));
          }}
        >
          <SortableContext items={chapters.map((c) => c.id)} strategy={verticalListSortingStrategy}>
            <ul className="space-y-3">
              {chapters.map((chapter, ci) => (
                <ManagerChapter
                  key={chapter.id}
                  chapter={chapter}
                  index={ci}
                  isExpanded={expanded.has(chapter.id)}
                  busy={busyChapterId === chapter.id}
                  sensors={sensors}
                  onToggle={() => toggleExpanded(chapter.id)}
                  onRename={(title) => renameChapter(chapter.id, title)}
                  onDelete={() => setDeleteChapterTarget(chapter)}
                  onAddScene={() => addScene(chapter.id)}
                  onSuggestScenes={() => suggestScenes(chapter)}
                  onReorderScenes={(ids) => reorderScenes(chapter.id, ids)}
                  onRenameScene={renameScene}
                  onDeleteScene={(scene) => setDeleteSceneTarget(scene)}
                  onSavePlanPoint={savePlanPoint}
                  onDeletePlanPoint={deletePlanPoint}
                  onTogglePlanPointLock={togglePlanPointLock}
                  onReorderPlanPoints={reorderPlanPoints}
                  onSuggestPlanPoints={suggestPlanPoints}
                  promises={promises}
                  onSetPlanPointPromise={setPlanPointPromise}
                />
              ))}
            </ul>
          </SortableContext>
        </DndContext>
      )}

      <ConfirmDialog
        isOpen={!!deleteChapterTarget}
        onClose={() => setDeleteChapterTarget(null)}
        onConfirm={() => deleteChapterTarget && performDeleteChapter(deleteChapterTarget)}
        title={t('chaptersFirst.confirmDeleteChapterTitle', 'Delete chapter?')}
        message={
          deleteChapterTarget
            ? t('chaptersFirst.confirmDeleteChapterBody', {
                title: deleteChapterTarget.title,
                count: (deleteChapterTarget.scenes ?? []).length,
                words: deleteChapterTarget.wordCount ?? 0,
                defaultValue: `Delete "${deleteChapterTarget.title}" and its ${
                  (deleteChapterTarget.scenes ?? []).length
                } scene(s) (${deleteChapterTarget.wordCount ?? 0} words)?`,
              })
            : ''
        }
        confirmVariant="danger"
      />

      <ConfirmDialog
        isOpen={!!deleteSceneTarget}
        onClose={() => setDeleteSceneTarget(null)}
        onConfirm={() => deleteSceneTarget && performDeleteScene(deleteSceneTarget)}
        title={t('chaptersFirst.confirmDeleteSceneTitle', 'Delete scene?')}
        message={
          deleteSceneTarget
            ? t('planManager.confirmDeleteSceneBody', {
                title: deleteSceneTarget.title,
                words: deleteSceneTarget.wordCount ?? 0,
                defaultValue: `Delete "${deleteSceneTarget.title}" (${
                  deleteSceneTarget.wordCount ?? 0
                } words)? This cannot be undone.`,
              })
            : ''
        }
        confirmVariant="danger"
      />

      {companionOpen && bookId && (
        <BookCompanion
          bookId={bookId}
          focus="plan"
          onClose={() => setCompanionOpen(false)}
          onBookChanged={() => fetchTree()}
        />
      )}
    </div>
  );
}

interface ManagerChapterProps {
  chapter: Chapter;
  index: number;
  isExpanded: boolean;
  busy: boolean;
  sensors: ReturnType<typeof useSensors>;
  onToggle: () => void;
  onRename: (title: string) => void;
  onDelete: () => void;
  onAddScene: () => void;
  onSuggestScenes: () => void;
  onReorderScenes: (ids: string[]) => void;
  onRenameScene: (sceneId: string, title: string) => void;
  onDeleteScene: (scene: Scene) => void;
  onSavePlanPoint: (sceneId: string, planPointId: string | null, content: string) => void;
  onDeletePlanPoint: (sceneId: string, planPointId: string) => void;
  onTogglePlanPointLock: (sceneId: string, point: ScenePlanPoint) => void;
  onReorderPlanPoints: (sceneId: string, ids: string[]) => void;
  onSuggestPlanPoints: (
    sceneId: string,
    opts: { mode: SuggestionMode; focusTag?: string }
  ) => Promise<TaggedSuggestion[]>;
  promises: BookPromise[];
  onSetPlanPointPromise: (sceneId: string, planPointId: string, promiseId: string | null) => void;
}

function ManagerChapter({
  chapter,
  index,
  isExpanded,
  busy,
  sensors,
  onToggle,
  onRename,
  onDelete,
  onAddScene,
  onSuggestScenes,
  onReorderScenes,
  onRenameScene,
  onDeleteScene,
  onSavePlanPoint,
  onDeletePlanPoint,
  onTogglePlanPointLock,
  onReorderPlanPoints,
  onSuggestPlanPoints,
  promises,
  onSetPlanPointPromise,
}: ManagerChapterProps) {
  const { t } = useTranslation();
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: chapter.id,
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };
  const scenes = useMemo(
    () => [...(chapter.scenes ?? [])].sort((a, b) => a.position - b.position),
    [chapter.scenes]
  );

  return (
    <li ref={setNodeRef} style={style}>
      <Card padding="md">
        <div className="flex items-center gap-2">
          <button
            type="button"
            {...attributes}
            {...listeners}
            className="text-gray-300 hover:text-gray-500 cursor-grab active:cursor-grabbing"
            aria-label={t('chaptersFirst.dragHandle', 'Drag to reorder')}
          >
            ⋮⋮
          </button>
          <button
            type="button"
            onClick={onToggle}
            aria-expanded={isExpanded}
            className="w-5 h-5 flex items-center justify-center text-gray-500 hover:text-gray-800"
          >
            {isExpanded ? '▾' : '▸'}
          </button>
          <span className="text-sm font-semibold text-gray-400 shrink-0">{index + 1}.</span>
          <div className="flex-1 min-w-0">
            <InlineEdit
              value={chapter.title}
              onCommit={onRename}
              className="text-left font-semibold text-gray-800 dark:text-gray-100 hover:text-indigo-600 truncate w-full"
              inputClassName="w-full bg-transparent border-b border-indigo-400 focus:outline-none font-semibold"
              ariaLabel={t('chaptersFirst.renameChapterHint', 'Click to rename')}
            />
          </div>
          <span className="text-xs text-gray-400 shrink-0">
            {scenes.length} {t('chaptersFirst.scenesLowercase', 'scenes')} ·{' '}
            {chapter.wordCount ?? 0} {t('chaptersFirst.words', 'words')}
          </span>
          <RemoveButton onClick={onDelete} label={t('common.delete', 'Delete')} />
        </div>

        {isExpanded && (
          <div className="mt-3 pl-7">
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={(event: DragEndEvent) => {
                const { active, over } = event;
                if (!over || active.id === over.id) return;
                const ids = scenes.map((s) => s.id);
                const oldIndex = ids.indexOf(String(active.id));
                const newIndex = ids.indexOf(String(over.id));
                if (oldIndex === -1 || newIndex === -1) return;
                onReorderScenes(arrayMove(ids, oldIndex, newIndex));
              }}
            >
              <SortableContext
                items={scenes.map((s) => s.id)}
                strategy={verticalListSortingStrategy}
              >
                <ul className="space-y-2">
                  {scenes.map((scene) => (
                    <ManagerScene
                      key={scene.id}
                      scene={scene}
                      sensors={sensors}
                      onRename={(title) => onRenameScene(scene.id, title)}
                      onDelete={() => onDeleteScene(scene)}
                      onSavePlanPoint={onSavePlanPoint}
                      onDeletePlanPoint={onDeletePlanPoint}
                      onTogglePlanPointLock={onTogglePlanPointLock}
                      onReorderPlanPoints={onReorderPlanPoints}
                      onSuggestPlanPoints={onSuggestPlanPoints}
                      promises={promises}
                      onSetPlanPointPromise={onSetPlanPointPromise}
                    />
                  ))}
                </ul>
              </SortableContext>
            </DndContext>
            <div className="flex items-center gap-3 mt-2">
              <button
                type="button"
                onClick={onAddScene}
                className="text-xs text-indigo-600 hover:text-indigo-800 dark:text-indigo-300"
              >
                + {t('chaptersFirst.addScene', 'Add scene')}
              </button>
              <button
                type="button"
                onClick={onSuggestScenes}
                disabled={busy}
                className="text-xs text-indigo-500 hover:text-indigo-700 disabled:opacity-50"
              >
                {busy ? '…' : t('planManager.suggestScenes', '✨ Suggest scenes')}
              </button>
            </div>
          </div>
        )}
      </Card>
    </li>
  );
}

interface ManagerSceneProps {
  scene: Scene;
  sensors: ReturnType<typeof useSensors>;
  onRename: (title: string) => void;
  onDelete: () => void;
  onSavePlanPoint: (sceneId: string, planPointId: string | null, content: string) => void;
  onDeletePlanPoint: (sceneId: string, planPointId: string) => void;
  onTogglePlanPointLock: (sceneId: string, point: ScenePlanPoint) => void;
  onReorderPlanPoints: (sceneId: string, ids: string[]) => void;
  onSuggestPlanPoints: (
    sceneId: string,
    opts: { mode: SuggestionMode; focusTag?: string }
  ) => Promise<TaggedSuggestion[]>;
  promises: BookPromise[];
  onSetPlanPointPromise: (sceneId: string, planPointId: string, promiseId: string | null) => void;
}

function ManagerScene({
  scene,
  sensors,
  onRename,
  onDelete,
  onSavePlanPoint,
  onDeletePlanPoint,
  onTogglePlanPointLock,
  onReorderPlanPoints,
  onSuggestPlanPoints,
  promises,
  onSetPlanPointPromise,
}: ManagerSceneProps) {
  const { t } = useTranslation();
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: scene.id,
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };
  const [newContent, setNewContent] = useState('');

  // AI suggestion modal (reuses the scene editor's ClaimSuggestionPanel).
  const [panelOpen, setPanelOpen] = useState(false);
  const [suggestions, setSuggestions] = useState<TaggedSuggestion[]>([]);
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState<SuggestionMode>('fresh');
  const [moreTag, setMoreTag] = useState<string | null>(null);

  const points = useMemo(
    () => [...(scene.planPoints ?? [])].sort((a, b) => a.position - b.position),
    [scene.planPoints]
  );

  const addNew = () => {
    const trimmed = newContent.trim();
    if (!trimmed) return;
    onSavePlanPoint(scene.id, null, trimmed);
    setNewContent('');
  };

  // Short bodies get fresh ideas; substantial bodies get continuation suggestions.
  const openPanel = async () => {
    const wordCount = (scene.body ?? scene.previewContent ?? '')
      .trim()
      .split(/\s+/)
      .filter(Boolean).length;
    const defaultMode: SuggestionMode = wordCount < 200 ? 'fresh' : 'continue';
    setMode(defaultMode);
    setPanelOpen(true);
    setLoading(true);
    setSuggestions([]);
    setSuggestions(await onSuggestPlanPoints(scene.id, { mode: defaultMode }));
    setLoading(false);
  };

  const refresh = async () => {
    setLoading(true);
    setSuggestions([]);
    setSuggestions(await onSuggestPlanPoints(scene.id, { mode }));
    setLoading(false);
  };

  const changeMode = async (next: SuggestionMode) => {
    if (next === mode) return;
    setMode(next);
    setLoading(true);
    setSuggestions([]);
    setSuggestions(await onSuggestPlanPoints(scene.id, { mode: next }));
    setLoading(false);
  };

  const moreOfTag = async (tag: string) => {
    setMoreTag(tag);
    const extra = await onSuggestPlanPoints(scene.id, { mode, focusTag: tag });
    setSuggestions((prev) => [...prev, ...extra]);
    setMoreTag(null);
  };

  return (
    <li ref={setNodeRef} style={style} className="rounded-md bg-gray-50 dark:bg-gray-800/40 p-2">
      <div className="flex items-center gap-2">
        <button
          type="button"
          {...attributes}
          {...listeners}
          className="text-gray-300 hover:text-gray-500 cursor-grab active:cursor-grabbing text-xs"
          aria-label={t('chaptersFirst.dragHandle', 'Drag to reorder')}
        >
          ⋮⋮
        </button>
        <HealthDot health={scene.health} />
        <div className="flex-1 min-w-0">
          <InlineEdit
            value={scene.title}
            onCommit={onRename}
            className="text-left text-sm text-gray-700 dark:text-gray-200 hover:text-indigo-600 truncate w-full"
            inputClassName="w-full bg-transparent border-b border-indigo-400 focus:outline-none text-sm"
            ariaLabel={t('chaptersFirst.renameSceneHint', 'Click to rename')}
          />
        </div>
        {scene.locked && <span title={t('chaptersFirst.locked', 'Locked')}>🔒</span>}
        <RemoveButton onClick={onDelete} label={t('common.delete', 'Delete')} />
      </div>

      <div className="ml-6 mt-1 space-y-1">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={(event: DragEndEvent) => {
            const { active, over } = event;
            if (!over || active.id === over.id) return;
            const ids = points.map((p) => p.id);
            const oldIndex = ids.indexOf(String(active.id));
            const newIndex = ids.indexOf(String(over.id));
            if (oldIndex === -1 || newIndex === -1) return;
            onReorderPlanPoints(scene.id, arrayMove(ids, oldIndex, newIndex));
          }}
        >
          <SortableContext items={points.map((p) => p.id)} strategy={verticalListSortingStrategy}>
            <ul className="space-y-0.5">
              {points.map((point) => (
                <ManagerPlanPoint
                  key={point.id}
                  point={point}
                  promises={promises}
                  onSave={(content) => onSavePlanPoint(scene.id, point.id, content)}
                  onDelete={() => onDeletePlanPoint(scene.id, point.id)}
                  onToggleLock={() => onTogglePlanPointLock(scene.id, point)}
                  onSetPromise={(promiseId) => onSetPlanPointPromise(scene.id, point.id, promiseId)}
                />
              ))}
            </ul>
          </SortableContext>
        </DndContext>

        <div className="flex items-center gap-2">
          <input
            type="text"
            value={newContent}
            onChange={(e) => setNewContent(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                addNew();
              }
            }}
            placeholder={t('chaptersFirst.planPoints.addPlaceholder', 'Add a plan point…')}
            className="flex-1 min-w-0 text-xs bg-transparent border-b border-gray-200 dark:border-gray-700 focus:border-indigo-400 focus:outline-none py-0.5"
          />
          <button
            type="button"
            onClick={openPanel}
            className="shrink-0 text-[11px] text-indigo-500 hover:text-indigo-700 disabled:opacity-50"
            title={t('chaptersFirst.planPoints.suggest', 'Suggest plan points')}
          >
            {t('chaptersFirst.planPoints.suggestShort', '✨ Suggest')}
          </button>
        </div>
      </div>

      <ClaimSuggestionPanel
        isOpen={panelOpen}
        onClose={() => setPanelOpen(false)}
        suggestions={suggestions}
        isLoading={loading}
        onAddSuggestion={async (content) => {
          await onSavePlanPoint(scene.id, null, content);
        }}
        onRefreshSuggestions={refresh}
        onMoreOfTag={moreOfTag}
        isLoadingMore={moreTag !== null}
        loadingMoreTag={moreTag}
        mode={mode}
        onChangeMode={changeMode}
      />
    </li>
  );
}

interface ManagerPlanPointProps {
  point: ScenePlanPoint;
  promises: BookPromise[];
  onSave: (content: string) => void;
  onDelete: () => void;
  onToggleLock: () => void;
  onSetPromise: (promiseId: string | null) => void;
}

function ManagerPlanPoint({
  point,
  promises,
  onSave,
  onDelete,
  onToggleLock,
  onSetPromise,
}: ManagerPlanPointProps) {
  const { t } = useTranslation();
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: point.id,
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };
  const [promiseMenuOpen, setPromiseMenuOpen] = useState(false);
  const linkedPromise = point.bookPromiseId
    ? promises.find((p) => p.id === point.bookPromiseId)
    : null;

  return (
    <li ref={setNodeRef} style={style} className="group/pp flex items-start gap-1 text-xs">
      <button
        type="button"
        {...attributes}
        {...listeners}
        className="text-gray-300 hover:text-gray-500 cursor-grab active:cursor-grabbing text-[10px] mt-0.5"
        aria-label={t('chaptersFirst.dragHandle', 'Drag to reorder')}
      >
        ⋮⋮
      </button>
      <span className="shrink-0 text-gray-300 mt-0.5">•</span>
      <div className="flex-1 min-w-0">
        <InlineEdit
          value={point.content}
          onCommit={onSave}
          className="text-left text-gray-600 dark:text-gray-300 hover:text-indigo-600 w-full"
          inputClassName="w-full bg-transparent border-b border-indigo-400 focus:outline-none"
        />
        {linkedPromise && (
          <span
            className="mt-0.5 inline-flex items-center gap-1 text-[10px] text-emerald-700 dark:text-emerald-400"
            title={t('bookPromises.delivers', 'Delivers a book promise')}
          >
            🎯 {linkedPromise.content}
          </span>
        )}
      </div>
      {promises.length > 0 && (
        <span className="relative shrink-0">
          <button
            type="button"
            onClick={() => setPromiseMenuOpen((o) => !o)}
            aria-label={t('bookPromises.linkPromise', 'Link to a book promise')}
            title={t('bookPromises.linkPromise', 'Link to a book promise')}
            className={`px-0.5 transition-opacity ${
              linkedPromise
                ? 'opacity-100 text-emerald-600'
                : 'text-gray-400 hover:text-indigo-600 opacity-100 lg:opacity-0 lg:group-hover/pp:opacity-100 focus:opacity-100'
            }`}
          >
            🎯
          </button>
          <DropdownMenu
            isOpen={promiseMenuOpen}
            onClose={() => setPromiseMenuOpen(false)}
            position="right"
            width="md"
          >
            {promises.map((p) => (
              <DropdownMenuItem
                key={p.id}
                onClick={() => {
                  onSetPromise(p.id === point.bookPromiseId ? null : p.id);
                  setPromiseMenuOpen(false);
                }}
              >
                <span className={p.id === point.bookPromiseId ? 'font-semibold' : ''}>
                  {p.id === point.bookPromiseId ? '✓ ' : ''}
                  {p.content}
                </span>
              </DropdownMenuItem>
            ))}
            {linkedPromise && (
              <DropdownMenuItem
                onClick={() => {
                  onSetPromise(null);
                  setPromiseMenuOpen(false);
                }}
              >
                <span className="text-gray-500">{t('bookPromises.unlink', 'Unlink')}</span>
              </DropdownMenuItem>
            )}
          </DropdownMenu>
        </span>
      )}
      <button
        type="button"
        onClick={onToggleLock}
        className="shrink-0 px-0.5 opacity-100 lg:opacity-0 lg:group-hover/pp:opacity-100 focus:opacity-100 transition-opacity"
        aria-label={
          point.locked ? t('chaptersFirst.unlock', 'Unlock') : t('chaptersFirst.lock', 'Lock')
        }
        title={point.locked ? t('chaptersFirst.unlock', 'Unlock') : t('chaptersFirst.lock', 'Lock')}
      >
        {point.locked ? '🔒' : '🔓'}
      </button>
      <span className="shrink-0 opacity-100 lg:opacity-0 lg:group-hover/pp:opacity-100 focus-within:opacity-100 transition-opacity">
        <RemoveButton onClick={onDelete} label={t('common.delete', 'Delete')} />
      </span>
    </li>
  );
}
